/* Deck View — a deck as something you *look at*.
 *
 * The builder is a workspace: the deck sits in a narrow rail next to the whole
 * ~2,665-card pool, because you are editing it. That is the wrong shape for a
 * deck someone handed you, which is what a share link is. Here the deck is the
 * page, and editing is one deliberate button.
 *
 * Deliberately absent: cost curve, stat strip, prices, "copy for sim". A deck
 * page is not a dashboard and not a storefront.
 */

import { h, fill } from './dom.js';
import { cardTile } from './card.js';
import { attachPreview } from './preview.js';
import { imageSources, getCards } from '../data/cards.js';
import { groupedDeck, totalCards, DECK_SIZE } from '../data/deck.js';

/* Characters, then Events, then Stages — and anything unexpected after, so a
 * new category can never silently vanish from the page. `groupedDeck` has
 * already sorted within each group by cost then name. */
const CATEGORY_ORDER = ['Character', 'Event', 'Stage'];

/**
 * Flat, ordered card list for the grid.
 *
 * The grouping exists to get the ordering; the boundaries are then thrown
 * away. The reference page renders one uninterrupted flow, and section
 * headings turn a 50-card list into three short lists that each need reading
 * separately.
 */
export function deckOrder(deck) {
  const groups = groupedDeck(deck);
  const keys = [...CATEGORY_ORDER, ...Object.keys(groups).filter(k => !CATEGORY_ORDER.includes(k))];
  return keys.flatMap(k => groups[k] || []);
}

/**
 * "20 days ago".
 *
 * Absolute timestamps make you do arithmetic to answer the only question
 * anyone asks of a deck list, which is whether it predates the last set.
 */
export function relativeTime(value, now = Date.now()) {
  const t = value instanceof Date ? value.getTime() : Date.parse(value);
  if (!Number.isFinite(t)) return null;

  const secs = Math.round((now - t) / 1000);
  if (secs < 0) return 'just now';          // clock skew reads better than "in -3 days"

  /* No "weeks". A list is stale relative to the last set, and days are how
   * people count that — "2 weeks ago" makes you multiply before you can decide
   * whether a deck predates it. */
  const units = [
    ['year', 31536000], ['month', 2592000],
    ['day', 86400], ['hour', 3600], ['minute', 60],
  ];
  for (const [name, size] of units) {
    const n = Math.floor(secs / size);
    if (n >= 1) return `${n} ${name}${n === 1 ? '' : 's'} ago`;
  }
  return 'just now';
}

export function createDeckView(app) {
  /* The deck on screen is not app.deck. That separation is the whole point:
   * looking at someone's list must not touch what you were building. */
  let deck = null;

  const els = {};

  /* ------------------------------------------------------------------ */
  /* header                                                              */
  /* ------------------------------------------------------------------ */

  function leaderThumb(leader) {
    if (!leader) return h('div.dv-thumb.is-empty', { 'aria-hidden': 'true' });

    const sources = imageSources(leader);
    if (!sources.length) return h('div.dv-thumb.is-empty', { 'aria-hidden': 'true' });

    // Same fallback chain as the tiles: try each source, then show nothing
    // rather than a broken-image icon.
    const img = h('img', { alt: '', loading: 'lazy', src: sources[0] });
    let i = 0;
    img.addEventListener('error', () => {
      i += 1;
      if (i < sources.length) img.src = sources[i];
      else img.remove();
    });
    return h('div.dv-thumb', img);
  }

  function byline() {
    const bits = [];

    if (deck.author) {
      /* rel=noopener on an author link that may point anywhere once decks are
       * server-backed. Cheap now, and easy to forget later. */
      bits.push(deck.authorUrl
        ? h('a.dv-author', { href: deck.authorUrl, rel: 'noopener noreferrer' }, deck.author)
        : h('span.dv-author', deck.author));
    }

    const when = relativeTime(deck.updatedAt);
    if (when) {
      if (bits.length) bits.push(h('span.dv-dot', { 'aria-hidden': 'true' }, '·'));
      bits.push(h('time.dv-when',
        { datetime: new Date(deck.updatedAt).toISOString?.() || '' }, when));
    }

    return bits.length ? h('div.dv-byline', bits) : null;
  }

  function tags() {
    const list = (deck.tags || []).filter(Boolean);
    if (!list.length) return null;
    return h('div.dv-tags', list.map(t => h('span.chip.dv-tag', String(t))));
  }

  function header() {
    const n = totalCards(deck);
    const db = getCards();
    const leader = deck.leader ? db[deck.leader] : null;

    return h('div.dv-head',
      leaderThumb(leader),
      h('div.dv-headtext',
        h('h2.dv-title', deck.name || 'Untitled deck'),
        byline(),
        deck.description ? h('p.dv-desc', deck.description) : null,
        tags(),
      ),
      h('div.dv-actions',
        /* The count is orientation, not analysis — "is this a full deck" is a
         * fair question to answer on a page you were handed. Anything past it
         * belongs in the builder. */
        h('div.dv-count', `${n}/${DECK_SIZE}${leader ? ` · ${leader.name}` : ''}`),
        h('div.dv-buttons',
          els.save = h('button.btn.dv-save', { onclick: () => saveImage() }, 'Save Image'),
          h('button.btn-pri.dv-edit', { onclick: () => edit() }, 'Edit Deck'),
        ),
        /* aria-live, because the outcome of "Save Image" is otherwise only
         * visible as a file appearing somewhere else entirely. */
        els.note = h('div.dv-note', { role: 'status', 'aria-live': 'polite', hidden: true }),
      ),
    );
  }

  /* ------------------------------------------------------------------ */
  /* grid                                                                */
  /* ------------------------------------------------------------------ */

  /**
   * One tile plus its quantity caption.
   *
   * The caption sits *below* the card rather than as an overlaid badge, so it
   * never covers art, and it reads as a caption instead of competing with the
   * cost and power the tile already draws.
   */
  function gridCell(card, qty) {
    const tile = attachPreview(cardTile(card, { as: 'div', hoverable: true }), card);
    return h('div.dv-cell',
      tile,
      h('div.dv-qty', qty == null ? '' : `${qty}x`),
    );
  }

  function grid() {
    const db = getCards();
    const cells = [];

    /* The Leader is the first tile in the same grid, not a hero block. It is
     * still a card in the list; the header thumbnail is the only special
     * treatment it gets. */
    const leader = deck.leader ? db[deck.leader] : null;
    if (leader) cells.push(gridCell(leader, 1));

    for (const { card, qty } of deckOrder(deck)) cells.push(gridCell(card, qty));

    if (!cells.length) {
      return h('div.empty-note', 'This deck is empty.');
    }
    return h('div.dv-grid', cells);
  }

  /* ------------------------------------------------------------------ */
  /* actions                                                             */
  /* ------------------------------------------------------------------ */

  /**
   * "Save Image" — the deck as a PNG (#76).
   *
   * The canvas module is loaded on demand. It is a few hundred lines nobody
   * needs to have downloaded to *look* at a deck, and — less cosmetically —
   * `deckimage.js` imports `deckOrder` from this file, so a static import here
   * would be a cycle. A dynamic one is not.
   *
   * The picture is drawn with our own card faces, never the printed artwork —
   * `ART_IN_EXPORT_ALLOWED` in `deckimage.js` holds that shut until #40 is
   * answered. The note still says which kind of picture it produced, because
   * without it the designed faces read as a bug rather than as the intended
   * output; it no longer blames the CDN, which stopped being the reason.
   */
  async function saveImage() {
    const btn = els.save;
    if (!btn || btn.disabled) return;
    const label = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Rendering…';
    note('');

    try {
      const mod = await import('./deckimage.js');
      const out = await mod.exportDeckImage(deck, {
        onProgress: ({ phase, done, total }) => {
          btn.textContent = phase === 'art' && total
            ? `Loading art ${done}/${total}…`
            : 'Rendering…';
        },
      });
      const how = await mod.saveDeckImage(out);
      if (how === 'cancelled') note('');
      else if (out.withArt) note('Saved.');
      else note('Saved — with Card Theory card faces, not the printed art.');
    } catch (err) {
      note(`Could not save the image — ${err?.message || 'something went wrong'}`);
    } finally {
      btn.disabled = false;
      btn.textContent = label;
    }
  }

  function note(text) {
    if (!els.note) return;
    els.note.textContent = text;
    els.note.hidden = !text;
  }

  async function edit() {
    /* Adopted as a *new* deck, not written over the one you had open.
     *
     * Under the single `localStorage` slot this assigned straight to app.deck,
     * which was the only thing it could do — and #12's whole guard-rail
     * existed because that destroyed whatever you were building. Now that a
     * deck list exists (#48), taking someone's list costs you nothing: yours
     * stays exactly where it was, and the shared one joins it.
     *
     * `adoptDeck` hands the builder its own copy, so the viewed deck never
     * becomes a live alias of app.deck through a shared `cards` object. */
    await app.adoptDeck({ ...deck, cards: { ...deck.cards } });

    /* Drop the share fragment now that this deck is yours, so a reload lands
     * in the builder rather than back on the read-only page. replaceState
     * does not fire hashchange, which is what makes this safe here — assigning
     * location.hash would route us straight back to Deck View. */
    try {
      history.replaceState(null, '', location.pathname + location.search);
    } catch { /* file:// or a sandboxed frame — the URL is cosmetic here */ }

    app.go('builder');
  }

  /* ------------------------------------------------------------------ */
  /* assembly                                                            */
  /* ------------------------------------------------------------------ */

  const root = h('div.view.view-deck', { id: 'view-deckview' });

  function render() {
    if (!deck) {
      fill(root, h('main.pane', h('div.empty-note', 'No deck to show.')));
      return;
    }
    fill(root, h('main.pane.dv-pane', header(), grid()));
  }

  return {
    root,
    render,
    /** Point the view at a deck. Does not touch `app.deck`. */
    show(d) { deck = d; render(); },
    /** The deck on screen is independent of the builder's, so this is a no-op. */
    onDeckChange() {},
    get deck() { return deck; },
  };
}
